import React, { useState, useEffect, useRef } from "react";
import SequenceableControls from "./SequenceableControls";
import DOMPurify from "dompurify";
import Icon from "@material-ui/core/Icon";
import Label from "./Label";

/**
 * <StatefulWeeklyPlan editMode={} handleChange={} removeItem={} updateWeeksJoins={} reqOptions={} modules={} mainLos={} focusKey={} focus={} updateWeeksJoins={} childTypes={} />
 */
const StatefulWeeklyPlan = (props) => {
  const {
    lastUpdateType,
    updateTime,
    api,
    reqOptions,
    syllabusId,
    unitChildOptions,
  } = props;
  const [weeks, setWeeks] = useState([]);
  const [modules, setModules] = useState(props.units || []);
  const [mainLos, setMainLos] = useState(props.mainLos || []);
  const [reload, setReload] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const [maxSequence, setMaxSequence] = useState(0);

  const _isMounted = useRef(false);

  const [modulesById, setModulesById] = useState(organizeByKey(modules, "id"));
  const [mainLosById, setMainLosById] = useState(organizeByKey(mainLos, "id"));
  const url = `${api}weeks`;

  /**
   * component mount/component unmount logic
   **/
  useEffect(function updateMounted() {
    _isMounted.current = true;
    return function cleanupMounted() {
      _isMounted.current = false;
    };
  }, []);

  useEffect(
    function modulesUpdate() {
      setModulesById(organizeByKey(modules, "id"));
    },
    [modules]
  );

  useEffect(
    function modPropUpdate() {
      if (props.units) setModules(props.units);
    },
    [props.units]
  );

  useEffect(
    function losUpdate() {
      setMainLosById(organizeByKey(mainLos, "id"));
    },
    [mainLos]
  );

  useEffect(
    function loPropUpdate() {
      if (props.mainLos) setMainLos(props.mainLos);
    },
    [props.mainLos]
  );

  useEffect(
    function fetchItems() {
      if (reload) {
        apiCall(
          `${url}?week[syllabus_id]=${syllabusId}`,
          reqOptions("GET"),
          (res) => {
            res.json().then((data) => {
              if (_isMounted.current) {
                setWeeks(data);
                setMaxSequence(data.length);
                setDataReady(true);
                setReload(false);
              }
            });
            setReload(false);
          }
        );
      }
    },
    [reload]
  );

  useEffect(
    function reloadEffect() {
      if (lastUpdateType === "week" && _isMounted.current) {
        setReload(true);
      }
      if (
        (lastUpdateType === "main_lo" ||
          lastUpdateType === "evid_of_learning") &&
        _isMounted.current
      ) {
        console.log("should update evid of learning in weekly plan");
        apiCall(
          `${api}main_los?main_lo[syllabus_id]=${syllabusId}`,
          reqOptions("GET"),
          (res) => {
            res.json().then((data) => {
              if (_isMounted.current) {
                setMainLos(data);
              }
            });
          }
        );
        setReload(true);
      }
      if (lastUpdateType === "unit" && _isMounted.current) {
        apiCall(
          `${api}units?unit[syllabus_id]=${syllabusId}`,
          reqOptions("GET"),
          (res) => {
            res.json().then((data) => {
              if (_isMounted.current) {
                setModules(data);
              }
            });
          }
        );
        setReload(true);
      } else if (
        (lastUpdateType || "").split("-")[0] === "unit_descendant" &&
        _isMounted.current
      ) {
        apiCall(
          `${api}units?unit[syllabus_id]=${syllabusId}`,
          reqOptions("GET"),
          (res) => {
            res.json().then((data) => {
              if (_isMounted.current) {
                setModules(data);
              }
            });
          }
        );
        setReload(true);
      }
    },
    [updateTime]
  );

  const weekElems = weeks.map((w, ix) => {
    const key = `week-${w.id}`;
    const inFocus = props.focusKey === key;

    const removeItem = (opts, e) => {
      if (confirm("Are you sure you want to remove this unit/module?")) {
        const { id } = opts;
        const body = {};
        body["week"] = { syllabus_id: syllabusId, active: false };
        apiCall(`${url}/${id}`, reqOptions("PATCH", body), (res) =>
          setReload(true)
        );
      }
    };

    const updateItemSequence = (opts, e) => {
      const { id, value } = opts;
      const body = {};
      body["week"] = { syllabus_id: syllabusId, sequence: value };
      apiCall(`${url}/${id}`, reqOptions("PATCH", body), (res) =>
        setReload(true)
      );
    };

    const controls = inFocus ? (
      <SequenceableControls
        editMode={props.editMode}
        removeItem={removeItem}
        item={w}
        itemIndex={ix}
        handleChange={updateItemSequence}
        maxSequence={maxSequence}
        type="week"
        plType="weeks"
      />
    ) : (
      ""
    );

    /** Table row mapping return **/
    return (
      <li
        id={key}
        className="mb-3"
        key={key}
        onClick={(e) => props.focus(key, e)}
      >
        <div className="card">
          <div className="card-header">
            <span className="sm-font">
              <strong>Week #{w.sequence}</strong>
            </span>
            {controls}
          </div>
          <div className="card-body">
            <ul className="list-group">
              <MenuControlledCell
                {...props}
                week={w}
                dataReady={dataReady}
                options={modules}
                optionsById={modulesById}
                inFocus={inFocus}
                parentKey={key}
                selectType="Unit"
                label="Module/Unit(s)"
              />
              <ChecklistList
                {...props}
                week={w}
                dataReady={dataReady}
                // modulesById={modulesById}
                parentsById={modulesById}
                parentClass="Unit"
                childType={unitChildOptions[0]}
                scheduleableClass="UnitDescendant"
                inFocus={inFocus}
              />
              <MenuControlledCell
                {...props}
                week={w}
                dataReady={dataReady}
                options={mainLos}
                optionsById={mainLosById}
                inFocus={inFocus}
                parentKey={key}
                selectType="MainLo"
                label="Course Learning Outcome(s)"
              />
              <ChecklistList
                {...props}
                week={w}
                dataReady={dataReady}
                parentsById={mainLosById}
                parentClass="MainLo"
                scheduleableClass="EvidOfLearning"
                label="Evidence(s) of Learning"
                inFocus={inFocus}
              />
              <ChecklistList
                {...props}
                week={w}
                dataReady={dataReady}
                // modulesById={modulesById}
                parentsById={modulesById}
                parentClass="Unit"
                childType={unitChildOptions[1]}
                scheduleableClass="UnitDescendant"
                inFocus={inFocus}
              />
              <ChecklistList
                {...props}
                week={w}
                dataReady={dataReady}
                // modulesById={modulesById}
                parentsById={modulesById}
                childType={unitChildOptions[2]}
                parentClass="Unit"
                scheduleableClass="UnitDescendant"
                inFocus={inFocus}
              />
            </ul>
          </div>
        </div>
      </li>
    );
  });

  /** <StatefulWeeklyPlan /> return **/
  return <ul className="list-unstyled">{weekElems}</ul>;
};

/**
 * Child components to render table cells where
 * adding items is done by dropdown. I.e.,
 *   - Modules and CLOs
 **/
/** <MenuControlledCell {...props} week={} options={} optionsById={} inFocus={} parentKey={} selectType={} /> */
const MenuControlledCell = (props) => {
  try {
    let joinCount = 0;
    const alreadyJoined = [];
    const joins = props.week.weeks_joins.map((j, ix) => {
      const item = props.optionsById[j.scheduleable_id];
      if (item && j["scheduleable_type"] === props.selectType) {
        alreadyJoined.push(j.scheduleable_id);
        joinCount++;
        return (
          <RemoveableJoin
            {...props}
            key={`${props.parentKey}-join-${j.id}`}
            joinId={j.id}
            joinText={item.sequence}
            joinTitle={DOMPurify.sanitize(item["name"] || item["description"], {
              ALLOWED_TAGS: [],
            })}
          />
        );
      }
      return null;
    });
    const itemSelect = props.inFocus ? (
      <WeekItemSelect
        {...props}
        week={props.week}
        alreadyJoinedIds={alreadyJoined}
      />
    ) : null;
    return props.inFocus || joinCount > 0 ? (
      <li className="list-unstyled mb-3">
        <div className="w-100 d-flex justify-content-between">
          <label className="text-bold">
            <strong>{props.label}</strong>
          </label>
          {itemSelect}
        </div>
        <ul className="ml-3 list-group">{joins}</ul>
      </li>
    ) : null;
  } catch (e) {
    console.error(e);
  }
};

/** <RemoveableJoin {...props} joinId={} joinText={} joinTitle={} isLast={} /> **/
const RemoveableJoin = (props) => {
  try {
    const changeOptions = {
      week: props.week,
      joinId: props.joinId,
      active: false,
    };
    let rmBtn = props.inFocus ? (
      <Icon
        className="mr-2 text-danger"
        fontSize="small"
        onClick={(e) => {
          props.updateWeeksJoins(changeOptions, e);
        }}
      >
        clear
      </Icon>
    ) : (
      ""
    );

    return (
      <li className="list-group-item d-flex align-items-center">
        <span className="p-1" title={`${props.label} #${props.joinText}`}>
          {props.joinTitle}
        </span>
        {rmBtn}
      </li>
    );
  } catch (e) {
    console.error(e);
  }
};

/**
 * <WeekItemSelect {...props} week={} options={} alreadyJoinedIds={} />
 **/
const WeekItemSelect = (props) => {
  try {
    const selectId = `add-week-${props.week.id}-${props.selectType}`;
    const changeOptions = {
      selectId: selectId,
      scheduleableType: props.selectType,
      week: props.week,
      active: true,
    };
    const options = props.options.map((opt) => {
      if (props.alreadyJoinedIds.indexOf(opt.id) === -1) {
        return (
          <option
            value={opt.id}
            key={`${props.parentKey}-${props.selectType}-${opt.id}`}
          >
            {opt.sequence}.{" "}
            {DOMPurify.sanitize(opt["name"] || opt["description"], {
              ALLOWED_TAGS: [],
            })}
          </option>
        );
      }
      return "";
    });

    return (
      <div
        onClick={(e) => props.focus(props.parentKey, e)}
        style={{ maxWidth: "10vw" }}
        className="d-flex"
      >
        <select
          id={selectId}
          aria-controls={`${selectId}-btn`}
          className="btn btn-secondary mr-1 mt-1 mb-1"
          style={{ maxWidth: "7vw" }}
        >
          {options}
        </select>
        <button
          id={`${selectId}-btn`}
          className="btn btn-primary mr-1 mt-1 mb-1"
          role="region"
          aria-labelledby={selectId}
          onClick={(e) => props.updateWeeksJoins(changeOptions, e)}
        >
          <Icon className="mr-2 text-light">add</Icon>
        </button>
      </div>
    );
  } catch (e) {
    console.error(e);
  }
};

/**
 * Child components to render table cells with
 * types of unit_descendants.
 **/

/**
 * <ChecklistCell week={}
 *   modulesById={}
 *   childType={}
 *   inFocus={}
 *   label={}
 *   mainLosById={}
 *   scheduleableClass={}
 * />
 */
const ChecklistList = (props) => {
  try {
    const { childType, label, parentClass, parentsById, scheduleableClass } =
      props;
    let joinsCount = 0;
    const joinsBySchType = getJoinsBySchType(props.week);
    // console.log('joinsBySchType')
    // console.log(joinsBySchType)
    // console.log('scheduleableClass', scheduleableClass)
    const joinsBySchId = organizeByKey(
      joinsBySchType[scheduleableClass],
      "scheduleable_id"
    );

    /**
     * 1. Iterate over modules/units joined to this week.
     * 2. Get all unit_descendants of the modules that match
     *    the selected prop.childType (i.e., type_lookup_id).
     **/
    const childrenOfType = joinsBySchType[parentClass].reduce((acc, join) => {
      const parent = parentsById[join.scheduleable_id];
      if (parent != null) {
        const children =
          childType != null
            ? parent["descendants_by_type"][props.childType.id]
            : parent["evid_of_learnings"];
        return children ? acc.concat(children) : acc;
      }
      return acc;
    }, []);

    /**
     * 1. Create checkboxes for every unit_descendant in childrenOfType.
     *   a. If the descendant is joined to the week the box should be checked.
     *   b. If not in edit mode, only labels of joined descendants should be
     *      visible, and nothing else.
     **/
    const boxes = childrenOfType.map((child) => {
      const key = `check-week-${props.week.id}-child-${child.id}`;
      const join = joinsBySchId[child.id];
      joinsCount += join ? 1 : 0;
      return (
        <Checkbox
          {...props}
          key={key}
          parentKey={key}
          label={child.description}
          child={child}
          joinId={join ? join.id : null}
        />
      );
    });

    return props.inFocus || joinsCount > 0 ? (
      <li className="list-unstyled mb-3">
        <label className="w-100">
          <strong>{label || `${props.childType.name}(s)`}</strong>
        </label>
        <ul className="ml-3 list-group">{boxes}</ul>
      </li>
    ) : null;
  } catch (e) {
    console.error(e);
  }
};

/**
 * <Checkbox {...props} child={} label={} joinId={} >
 **/
const Checkbox = (props) => {
  try {
    const key = `${props.parentKey}-${props.scheduleableClass}-chkbx-${props.child.id}`;
    const changeOpts =
      props.joinId === null
        ? {
            scheduleableType: props.scheduleableClass,
            scheduleableId: props.child.id,
            week: props.week,
            active: true,
          }
        : {
            week: props.week,
            joinId: props.joinId,
            active: false,
          };

    let input = props.inFocus ? (
      <input
        id={key}
        className="mr-3"
        type="checkbox"
        onChange={(e) => props.updateWeeksJoins(changeOpts, e)}
        checked={props.joinId != null}
      />
    ) : (
      ""
    );

    return props.inFocus || props.joinId ? (
      <li className="list-group-item">
        <div className="d-flex justify-content-left align-items-center">
          {input || ""}
          <label
            htmlFor={key}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(props.child.description),
            }}
          />
        </div>
      </li>
    ) : (
      ""
    );
  } catch (e) {
    console.error(e);
  }
};

const getJoinsBySchType = (week) => {
  try {
    return week.weeks_joins.reduce(
      (acc, join) => {
        acc[join.scheduleable_type].push(join);
        return acc;
      },
      { Unit: [], UnitDescendant: [], MainLo: [], EvidOfLearning: [] }
    );
  } catch (e) {
    console.error(e);
  }
};

const organizeByKey = (items, key) => {
  try {
    return items.reduce((acc, i) => (acc[i[key]] = i) && acc, {});
  } catch (e) {
    console.error(e);
  }
};

const apiCall = (url, reqOptions, next) => {
  try {
    fetch(url, reqOptions).then((res) => {
      if (res.status === 401) {
        // alert('Unauthorized. Either your session has expired or you are not authorized to perform this action. Please try logging out and logging in again.')
        // if (confirm('Unauthorized. Either your session has expired or you are not authorized to perform this action. Redirect to login page? \n(Please note: if your session has expired, none of the changes you make to this syllabus will be saved until you log in again.)')) {
        //   sylContainer.props.history.push('/log_in');
        // }
        alert(
          "Not authorized to update the syllabus. Please reload the page and try again."
        );
      } else if (res.status >= 400) {
        alert(
          "There was an issue updating the syllabus. Please reload the page and try again."
        );
      } else if (next) {
        next(res);
      }
    });
  } catch (err) {
    //To Do: handle error
    console.error(err);
  }
};

export default StatefulWeeklyPlan;
